import { KeyedLoaded } from "common/loader";
import { ActiveLocation, ParentLocationUpdateStatus } from "../types/ActiveLocation";
import { TrackEventData } from "common/appInsights/types/TrackEventData";
import { Role } from "features/fakeRoles/types/RolesType";

export type State = KeyedLoaded<ActiveLocation, string | undefined>;

export enum TypeKeys {
    BEGIN = "LOCATION/FETCH_ACTIVE_BEGIN",
    SUCCESS = "LOCATION/FETCH_ACTIVE_SUCCESS",
    OVERRIDE_PERMISSION = "LOCATION/OVERRIDE_PERMISSION",
    FAILURE = "LOCATION/FETCH_ACTIVE_FAILURE",
    SET_LAST_ITEM_UPDATED = "LOCATION/SET_LAST_ITEM_UPDATED",
    UPDATE_STATUS_SUCCESS = "LOCATION/FETCH_UPDATE_STATUS_SUCCESS",
}

export const createAction = {
    begin: (slug: string | undefined) => ({ type: TypeKeys.BEGIN, slug }),
    success: (data: ActiveLocation, trackingData?: TrackEventData) => ({ type: TypeKeys.SUCCESS, data, trackingData }),
    overridePermission: (role?: Role) => ({ type: TypeKeys.OVERRIDE_PERMISSION, role }),
    failure: (slug: string | undefined, error: any) => ({ type: TypeKeys.FAILURE, error, slug }),
    setLastItemUpdated: (timeUtc: string) => ({ type: TypeKeys.SET_LAST_ITEM_UPDATED, timeUtc }),
    parentUpdateStatusSuccess: (data: ParentLocationUpdateStatus) => ({
        type: TypeKeys.UPDATE_STATUS_SUCCESS,
        data,
    }),
};

type FetchActiveBeginAction = { type: TypeKeys.BEGIN; slug: string | undefined };
export type FetchActiveSuccessAction = { type: TypeKeys.SUCCESS; data: ActiveLocation };
type FetchActiveFailureAction = { type: TypeKeys.FAILURE; error: any; slug: string | undefined };
type OverridePermissionAction = { type: TypeKeys.OVERRIDE_PERMISSION; role?: Role };

type FetchActiveAction = FetchActiveBeginAction | FetchActiveSuccessAction | FetchActiveFailureAction;

type SetLastItemUpdated = { type: TypeKeys.SET_LAST_ITEM_UPDATED; timeUtc: string };

type FetchParentUpdateStatusSuccessAction = {
    type: TypeKeys.UPDATE_STATUS_SUCCESS;
    data: ParentLocationUpdateStatus;
};

const initialState: State = {
    status: "unloaded",
};

export function reducer(
    state: State = initialState,
    action: FetchActiveAction | SetLastItemUpdated | FetchParentUpdateStatusSuccessAction | OverridePermissionAction
): State {
    if (action.type === TypeKeys.BEGIN) {
        return {
            status: "loading",
            key: action.slug,
        };
    }

    if (action.type === TypeKeys.SUCCESS && state.status !== "unloaded") {
        if (!state.key || state.key === action.data.slug || state.key === action.data.id) {
            return {
                status: "loaded",
                key: action.data?.slug || "*New*",
                data: action.data,
            };
        }
    }

    if (action.type === TypeKeys.UPDATE_STATUS_SUCCESS) {
        if (state.status === "loaded" && action.data) {
            return {
                ...state,
                data: {
                    ...state.data,
                    parentLocationUpdateStatus: action.data,
                },
            };
        }
    }

    if (action.type === TypeKeys.FAILURE && state.status !== "unloaded") {
        if (!state.key || state.key === action.slug) {
            return {
                ...state,
                status: "failed",
                error: action.error,
            };
        }
    }

    if (action.type === TypeKeys.SET_LAST_ITEM_UPDATED && state.status === "loaded") {
        if (state.data.lastItemUpdatedUtc !== action.timeUtc) {
            return {
                ...state,
                data: {
                    ...state.data,
                    lastItemUpdatedUtc: action.timeUtc,
                },
            };
        }
    }

    if (action.type === TypeKeys.OVERRIDE_PERMISSION && state.status === "loaded") {
        if (action.role || state.data.origAuthInfo) {
            const origAuthInfo = action.role ? state.data.origAuthInfo ?? state.data.authInfo : undefined;
            return {
                ...state,
                data: {
                    ...state.data,
                    origAuthInfo,
                    authInfo: !action.role
                        ? state.data.origAuthInfo!
                        : {
                              userRoles: [action.role.shortName],
                              permissions: action.role.permissions,
                          },
                },
            };
        }
    }

    return state;
}
